import React, { useState, useRef, useCallback } from 'react';
import { cn } from '../../../ui/utils/cn';
import { positionToRating } from '../utils';

interface CenteredDotRatingControlProps {
    position: number;
    onChange: (rating: number, position: number) => void;
    leftLabel: string;
    rightLabel: string;
    label: string;
    maxRange?: number;
}

const formatLabel = (label: string): string => {
    return label
        .replace(/-/g, ' ')
        .replace(/\b\w/g, c => c.toUpperCase());
};

export const CenteredDotRatingControl: React.FC<CenteredDotRatingControlProps> = ({
    position,
    onChange,
    leftLabel,
    rightLabel,
    label,
    maxRange = 5,
}) => {
    const clampedPos = Math.max(-maxRange, Math.min(maxRange, position));
    const rating = positionToRating(clampedPos, maxRange);

    const [hoverPos, setHoverPos] = useState<number | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    const positions = Array.from({ length: maxRange * 2 + 1 }, (_, idx) => idx - maxRange);

    const handleDotClick = (posVal: number) => {
        const newRating = positionToRating(posVal, maxRange);
        onChange(newRating, posVal);
    };

    const getPosFromX = useCallback((clientX: number): number => {
        if (!containerRef.current) return 0;
        const rect = containerRef.current.getBoundingClientRect();
        const x = clientX - rect.left;
        const width = rect.width;
        const segmentCount = maxRange * 2 + 1;
        const segmentIndex = Math.floor((x / width) * segmentCount);
        const clampedIndex = Math.max(0, Math.min(segmentCount - 1, segmentIndex));
        return clampedIndex - maxRange;
    }, [maxRange]);

    const handleDragStart = (clientX: number) => {
        setIsDragging(true);
        const posVal = getPosFromX(clientX);
        handleDotClick(posVal);
    };

    const handleDragMove = (clientX: number) => {
        if (!isDragging) return;
        const posVal = getPosFromX(clientX);
        handleDotClick(posVal);
    };

    const handleDragEnd = () => {
        setIsDragging(false);
    };

    const isFilled = (dotPos: number) => {
        const activePos = hoverPos !== null ? hoverPos : clampedPos;
        if (activePos === 0) return dotPos === 0;
        if (activePos < 0) {
            return dotPos <= 0 && dotPos >= activePos;
        }
        return dotPos >= 0 && dotPos <= activePos;
    };

    // color: center = green (perfect), extremes = orange/warm
    const getDotColor = (dotPos: number, filled: boolean) => {
        if (!filled) return 'bg-gray-200';
        const distance = Math.abs(dotPos);
        if (distance === 0) return 'bg-emerald-400';
        if (distance <= 1) return 'bg-emerald-300';
        if (distance <= 2) return 'bg-amber-300';
        if (distance <= 3) return 'bg-amber-400';
        return 'bg-orange-400';
    };

    return (
        <div className="space-y-1.5">
            <div className="flex items-center justify-between">
                <label className="text-sm font-medium">
                    {formatLabel(label)}
                </label>
                <span className={`text-xs font-semibold tabular-nums px-2 py-0.5 rounded-full ${
                    rating >= 8 ? 'bg-emerald-100 text-emerald-700'
                        : rating >= 5 ? 'bg-amber-100 text-amber-700'
                        : 'bg-red-100 text-red-700'
                }`}>
                    {rating}/10
                </span>
            </div>
            <div
                ref={containerRef}
                className="flex w-full gap-0.5 items-center touch-manipulation select-none"
                onMouseDown={(e) => handleDragStart(e.clientX)}
                onMouseMove={(e) => handleDragMove(e.clientX)}
                onMouseUp={handleDragEnd}
                onMouseLeave={() => { setHoverPos(null); handleDragEnd(); }}
                onTouchStart={(e) => e.touches.length === 1 && handleDragStart(e.touches[0].clientX)}
                onTouchMove={(e) => e.touches.length === 1 && handleDragMove(e.touches[0].clientX)}
                onTouchEnd={handleDragEnd}
            >
                {positions.map((posVal) => {
                    const filled = isFilled(posVal);
                    const isCenter = posVal === 0;
                    return (
                        <button
                            key={posVal}
                            type="button"
                            className={cn(
                                "flex-1 rounded-sm cursor-pointer transition-all duration-150",
                                isCenter ? "h-7" : "h-5",
                                getDotColor(posVal, filled)
                            )}
                            onMouseEnter={() => setHoverPos(posVal)}
                            onMouseLeave={() => setHoverPos(null)}
                            onClick={() => handleDotClick(posVal)}
                        />
                    );
                })}
            </div>
            <div className="flex justify-between items-center text-[11px] text-muted-foreground">
                <span>{leftLabel}</span>
                <span className="text-emerald-600 font-medium text-[10px]">perfect</span>
                <span>{rightLabel}</span>
            </div>
        </div>
    );
};
